import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import OtpInput from "react-otp-input";
import { Spinner, Button, InputGroup, Form } from "react-bootstrap";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";

import Background from "../../BackGround/background";
import MadeInLoveIndia from "../../common/madeInLoveIndia";
import Timer from "../../common/timer";
import authService from "../../../services/authService";
import flowBackEndServices2 from "../../../services/flowBackendServices";
import {
  loginEvent,
  logout,
  setEmailAndMobile,
  setOtpData,
  switchUserEvent,
} from "../../../slices/authSlice";
import { getBrowserDetails } from "../../../comman/status";

import "../mpin.css";
import { RootState } from "../../../store";
import { VerifyMfaResponse } from "../../../models/login";
import RiskDisclosure from "../../LoginForm/RiskDisclosure";
import { getExtraQueryparam } from "../../../components/common/parseQueryParam";
import moment from "moment";

// ICONS
import { ReactComponent as Calander } from "../../../assets/images/calendar.svg";
import ValidationList from "../../../components/ValidationList";
import { t } from "../../../utils/appString";

// Existing interfaces
interface LocationState {
  type?: string;
}

interface UserData {
  user_id: string;
  user_name: string;
}

interface AuthData {
  mfaType: "otp" | "totp";
  deviceId: string;
  refreshToken: string;
}

// Function to check if all characters are the same
const isAllSame = (value: string) => [...new Set(value)].length === 1;

// Function to check if the value is sequential (increasing or decreasing)
const isSequential = (value: string) => {
  const increasing = "0123456789";
  const decreasing = "9876543210";
  return increasing.includes(value) || decreasing.includes(value);
};

// Zod validation schema
const MpinRegistrationSchema = z
  .object({
    dateOfBirth: z
      .string()
      .transform((date) => {
        if (moment(date, "YYYY-MM-DD", true).isValid()) {
          return moment(date, "YYYY-MM-DD").format("YYYY-MM-DD"); // Ensure YYYY-MM-DD format
        } else if (moment(date, "DD/MM/YYYY", true).isValid()) {
          return moment(date, "DD/MM/YYYY").format("YYYY-MM-DD"); // Convert DD/MM/YYYY → YYYY-MM-DD
        }
        return date; // If invalid, return as-is (will be caught in validation)
      })
      .refine((date) => moment(date, "YYYY-MM-DD", true).isValid(), {
        message: "Date must be in YYYY-MM-DD or DD/MM/YYYY format",
      })
    // .refine((date) => {
    //   const birthDate = moment(date, "YYYY-MM-DD");
    //   return birthDate.isBefore(moment().subtract(18, "years"));
    // }, { message: "You must be at least 18 years old" })
    ,

    mpin: z
      .string()
      .length(4, "MPIN must be exactly 4 digits")
      .regex(/^\d+$/, "MPIN must contain only numbers")
      .refine((mpin) => !isAllSame(mpin), "MPIN should not have all the same digits")
      .refine((mpin) => !isSequential(mpin), "MPIN should not have sequential digits"),

    confirmMpin: z
      .string()
      .length(4, "Confirm MPIN must be exactly 4 digits")
      .regex(/^\d+$/, "Confirm MPIN must contain only numbers")
      .refine((mpin) => !isAllSame(mpin), "Confirm MPIN should not have all the same digits")
      .refine((mpin) => !isSequential(mpin), "Confirm MPIN should not have sequential digits"),
  })
  .refine((data) => data.mpin === data.confirmMpin, {
    message: "MPINs do not match",
    path: ["confirmMpin"],
  });


type MpinRegistrationFormData = z.infer<typeof MpinRegistrationSchema>;

const MpinInput: React.FC = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const tvTerminalId = process.env.REACT_APP_TV_APPLICATION_ID

  const { data, themeData, isOTPVerified } = useSelector((state: RootState) => state.authUser);

  const [showNext, setShowNext] = useState(false);
  const [kra, setKra] = useState<number>(0);
  const [showDot, setShowDot] = useState(false);
  const [showDisclosure, setShowDisclosure] = useState(false);
  const [aeroUrl, setAeroUrl] = useState<string | null>(null);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  const handleResize = () => {
    setIsDesktop(window.innerWidth > 768)
  };

  const handleLogoutToLogin = () => {
    if (!themeData || themeData.applicationId === tvTerminalId) {
      dispatch(switchUserEvent());
      dispatch(authService.logout());
      localStorage.removeItem('PERSIST_OBJECT');
      localStorage.removeItem("useRefresh")
      navigate("/", { replace: true });
    }
    else {
      navigate("/", { replace: true });
    }
  };

  // useEffect(() => {
  //   // lets check for local storage
  //   if (!isOTPVerified) {
  //     const localVAl = localStorage.getItem("TV_ACS_TOKEN")
  //     if (localVAl) {
  //       navigate("/apps", { replace: true });
  //     } else {

  //       handleLogoutToLogin()
  //     }
  //   }

  // }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [otpType, setOtpType] = useState<"otp" | "totp">("otp");

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<MpinRegistrationFormData>({
    resolver: zodResolver(MpinRegistrationSchema),
    mode: "onChange",
    defaultValues: {
      dateOfBirth: "",
      mpin: "",
      confirmMpin: "",
    },
  });

  const handleOnClick = async () => {
    // now for api call;
    if (!data) return;
    try {
      setIsLoading(true);

      let response;
      if (themeData.verifySSO) {
        response = await flowBackEndServices2.secondFactorSso(
          {
            dob: new Date(moment(watch("dateOfBirth"), "DD/MM/YYYY").format("YYYY-MM-DD")),
            mpin: "",
            newMpin: watch("confirmMpin"),
          },
          data.token
        );
      } else {
        response = await flowBackEndServices2.secondFactor(
          {
            dob: new Date(moment(watch("dateOfBirth"), "DD/MM/YYYY").format("YYYY-MM-DD")),
            mpin: "",
            newMpin: watch("confirmMpin"),
          },
          data.token
        );
      }
      if (response.data.status === "success") {
        // Handle success response
        setUpSessionData(response.data);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false);
    }
  };

  const GoBack = () => {
    navigate("/MPin", { replace: true });
    sessionStorage.removeItem("COMING_FROM_RESET_MPIN");
  };

  return (
    <>
      <Background>
        <div className="h-100 d-flex card_padding flex-column justify-content-between align-items-center">
          <div className="w-100 d-flex flex-row justify-content-left align-items-center gap-3">
            <i onClick={GoBack} className="fas fa-arrow-left fas fa-lg" style={{ cursor: "pointer" }} />
            <span
              style={{ color: "#171D27", fontWeight: 700 }}
              className="h5 pb-0 mb-0"
            >
              Reset MPIN
            </span>
          </div>

          <div className="flex-fill w-100 pt-5">
            {/* Date of Birth Field */}
            <div className="mb-3">
              <label
                htmlFor="dateOfBirth"
                className="form-label"
                style={{ fontWeight: 600, color: "black" }}
              >
                Date of Birth
              </label>

              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field: { onChange, value, ...field } }) => {
                  const [inputType, setInputType] = useState<"text" | "date">(
                    "text"
                  );
                  const inputRef = useRef<HTMLInputElement>(null);

                  const formatDate = (inputDate: string) => {
                    const digits = inputDate.replace(/\D/g, "");
                    if (digits.length === 8) {
                      const formattedDate = moment(digits, "YYYYMMDD").format(
                        "DD/MM/YYYY"
                      );
                      if (moment(formattedDate, "DD/MM/YYYY", true).isValid()) {
                        return formattedDate;
                      }
                    }
                    return "";
                  };

                  return (
                    <InputGroup>
                      <Form.Control
                        {...field}
                        ref={inputRef}
                        type={inputType}
                        placeholder="DD/MM/YYYY"
                        value={value}
                        onFocus={() => {
                          setInputType("date");
                          setTimeout(() => inputRef.current?.showPicker(), 100); // Open calendar picker
                        }}
                        onBlur={(e) => {
                          const formattedDate = formatDate(e.target.value);
                          onChange(formattedDate);
                          setInputType("text");
                        }}
                        onChange={(e) => onChange(e.target.value)}
                        maxLength={10}
                        className={`dateofbirth ${errors.dateOfBirth ? "is-invalid" : ""
                          }`}
                        id="dateOfBirth"
                        style={{
                          paddingRight: "40px",
                          backgroundColor: "white",
                          color: "#495057",
                        }}
                      />
                      {!errors.dateOfBirth && (
                        <InputGroup.Text
                          onClick={() => inputRef.current?.showPicker()} // Open date picker on click
                          style={{
                            position: "absolute",
                            right: 0,
                            zIndex: 12,
                            bottom: 5,
                            backgroundColor: "transparent",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          <Calander />
                        </InputGroup.Text>
                      )}
                    </InputGroup>
                  );
                }}
              />
              {errors.dateOfBirth && (
                <div className="invalid-feedback">
                  {errors.dateOfBirth.message}
                </div>
              )}
            </div>

            {/* Enter MPIN Field */}
            <div className="mb-3">
              <div className="w-100 d-flex flex-row justify-content-between align-items-center">
                <div style={{ fontWeight: 600 }} className="colorBlack">
                  Enter new MPIN
                </div>
              </div>
              <p style={{ color: "#A4B1C0", fontSize: 14 }}>
                Your new MPIN must differ from the last 3 MPINs.
              </p>
              <Controller
                name="mpin"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div
                    id="myInput"
                    className="w-100 d-flex flex-row align-items-center"
                  >
                    <OtpInput
                      inputStyle={`opt_box-hide ${errors.mpin ? "is-invalid" : ""}`}
                      value={value}
                      onChange={onChange}
                      numInputs={4}
                      isInputNum={true}
                      isInputSecure={false}
                      shouldAutoFocus={true}
                      separator={<span />}
                    />
                  </div>
                )}
              />
              {
                <ValidationList
                  isValid={
                    isAllSame(watch("mpin"))
                      ? false
                      : watch("mpin").length === 4
                        ? true
                        : false
                  }
                  message={t(
                    "validation.mpin.erroe.mpinShouldNotHaveAllSameDigits"
                  )}
                />
              }
              {
                <ValidationList
                  isValid={watch("mpin").length === 4 ? true : false}
                  message={t(
                    "validation.mpin.erroe.mpinShouldContainOnlyNumber"
                  )}
                />
              }
              {
                <ValidationList
                  isValid={
                    isSequential(watch("mpin"))
                      ? false
                      : watch("mpin").length === 4
                        ? true
                        : false
                  }
                  message={t(
                    "validation.mpin.erroe.mpinShouldNotHaveSequentialDigits"
                  )}
                />
              }
            </div>

            {/* Confirm MPIN Field */}
            <div className="mb-3">
              <div className="w-100 d-flex flex-row justify-content-between align-items-center">
                <div style={{ fontWeight: 600 }} className="colorBlack">
                  Confirm MPIN
                </div>
              </div>
              <Controller
                name="confirmMpin"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div
                    id="myInput"
                    className="w-100 d-flex flex-row align-items-center"
                  >
                    <OtpInput
                      inputStyle={`opt_box-hide ${errors.confirmMpin ? "is-invalid" : ""
                        }`}
                      value={value}
                      onChange={onChange}
                      numInputs={4}
                      isInputNum={true}
                      isInputSecure={false}
                      separator={<span />}
                    />


                  </div>
                )}
              />
              {watch("confirmMpin").length === 4 && errors.confirmMpin && (
                <div className="text-danger">{errors.confirmMpin.message}</div>
              )}
            </div>

            <Button
              id="myBtn"
              disabled={isLoading || !isValid}
              className="blue_btn w-100 my-3"
              onClick={handleOnClick}
              variant="primary"
              type="submit"
            >
              {isLoading && (
                <Spinner
                  className="mr-2"
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              )}
              Reset MPIN
            </Button>
          </div>
        </div>
      </Background>
      {showDisclosure && getDisclosure()}
    </>
  );

  function setUpSessionData(value: any) {
    let userData = value;
    if (userData.status === "success") {
      if (themeData.verifySSO) {
        const params = localStorage.getItem("Q_PARAM_ALL");
        const applicationId = params?.split("=");
        if (applicationId && applicationId[1] == 'at_g4FXzp00jB"') {
          if (value.kra_status == "HOLD") {
            setShowNext(true);
            setKra(1);
            setShowDot(true);
          } else if (value.kra_status == "REJECTED") {
            setShowNext(true);
            setKra(0);
            setShowDot(true);
          } else {
            setShowNext(false);
          }
          setShowDisclosure(true);
          setAeroUrl(value?.redirectUrl + getExtraQueryparam());
        } else {
          let fullRedirectUrl = value?.redirectUrl + getExtraQueryparam();
          window.open(fullRedirectUrl, "_self");
        }
        return;
      }
      if (value.data.kra_status == "HOLD") {
        setShowNext(true);
        setKra(1);
        setShowDot(true);
      } else if (value.data.kra_status == "REJECTED") {
        setShowNext(true);
        setKra(0);
        setShowDot(true);
      } else {
        setShowNext(false);
      }
      setShowDisclosure(true);

      let fullRedirectUrl = value?.redirect_url + getExtraQueryparam();
      let newData = {
        ...data,
        refreshToken: userData.refreshToken,
        redirectURL: fullRedirectUrl,
      };
      let dataSet = { data: newData, user_id: data.clientCode };
      dispatch(setOtpData(dataSet));
      const refreshData = {
        refreshToken: userData.refreshToken,
        ssoToken: userData.ssoToken,
        deviceId: data.deviceId,
        tradingActive: userData.data?.tradingActive,
      };
      const userModify = { ...userData.data, ...refreshData };

      // login event
      dispatch(setEmailAndMobile({ mobile: null, email: userModify.email }));
      dispatch(
        loginEvent({
          mobile: "",
          email: userModify.email,
        })
      );
      const object = {
        refresh_expires_at: userData.refresh_expires_at,
        user_name: userData.data.user_name,
        mobile: userData.data.mobile,
        client_code: userData.data.user_id,
      }
      if (!themeData || themeData.applicationId === tvTerminalId) {
        localStorage.setItem('PERSIST_OBJECT', JSON.stringify(object))
        localStorage.setItem("useRefresh", "true");
        sessionStorage.setItem("COMING_FROM_MPIN", "true");
      }
      authService.saveUserSessionFlow(userModify, refreshData, dispatch);
    }
  }

  function acknowledge() {
    localStorage.setItem("acknowledge_disclosure", "false");
    sessionStorage.setItem("acknowledge_disclosure", "false");
    if (aeroUrl) {
      window.open(aeroUrl, "_self");
    } else {
      // we will navigate to the set passowrd here
      navigate("/apps", { replace: true });
      // if(otpType === 'otp') navigate("/setTOtp",{ replace: true})
      // if(otpType === 'totp')  navigate("/tvTerminal",{ replace: true});
    }
  }

  function moveToNext() {
    setShowNext(false);
  }

  function getDisclosure() {
    localStorage.setItem("acknowledge_disclosure", "true");
    sessionStorage.setItem("acknowledge_disclosure", "true"); //To block back from browser
    if (isDesktop) {
      return (
        <React.Fragment>
          <div className="disclosureLayer"></div>
          <div className="disclosure">
            {/* {showNext && <KraStatus kraStatus={kra} />} */}
            {!showNext && <RiskDisclosure />}
            <div className="dot-container">
              {/* <div id="first-dot" className="cr-dot"></div> */}
              {showNext && <div id="second-dot" className="cr-dot"></div>}
            </div>
            {!showNext ? (
              <div className="discFooter">
                <div className="ackButton c-pointer" onClick={acknowledge}>
                  I understand
                </div>
              </div>
            ) : (
              <div className="discFooter">
                <div className="ackButton c-pointer" onClick={moveToNext}>
                  Next
                </div>
              </div>
            )}
          </div>
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          <div className="disclosureLayer"></div>
          <div className="disclosure" style={{ width: "300px" }}>
            {/* {showNext && <KraStatus kraStatus={kra} />} */}
            {!showNext && <RiskDisclosure />}
            <div className="dot-container">
              {/* <div id="first-dot" className="cr-dot"></div> */}
              {showNext && <div id="second-dot" className="cr-dot"></div>}
            </div>
            {!showNext ? (
              <div className="discFooter">
                <div className="ackButton c-pointer" onClick={acknowledge}>
                  I understand
                </div>
              </div>
            ) : (
              <div className="discFooter">
                <div className="ackButton c-pointer" onClick={moveToNext}>
                  Next
                </div>
              </div>
            )}
          </div>
        </React.Fragment>
      );
    }
  }
};

export default MpinInput;
